import React, { useCallback, useRef } from "react";

import { IconButton, Tooltip } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { FiVideoOff, FiVideo } from "react-icons/fi";

import useDevices from "../../hooks/useDevices/useDevices";
import useLocalVideoToggle from "../../hooks/useLocalVideoToggle/useLocalVideoToggle";

const useStyles = makeStyles(() =>
  createStyles({
    iconPointer: {
      cursor: "pointer",
    },
    iconButton: {
      padding: 0,
      marginLeft: "0.7rem",
      backgroundColor: "transparent",
      "&:hover": {
        backgroundColor: "transparent",
      },
      "&:disabled": {
        opacity: 0.8,
      },
    },
  })
);

export default function ToggleVideoButton(props: { disabled?: boolean }) {
  const [isVideoEnabled, toggleVideoEnabled] = useLocalVideoToggle();
  const lastClickTimeRef = useRef(0);
  const { hasVideoInputDevices } = useDevices();
  const { t: translate } = useTranslation();

  const toggleVideo = useCallback(() => {
    if (Date.now() - lastClickTimeRef.current > 500) {
      lastClickTimeRef.current = Date.now();
      toggleVideoEnabled();
    }
  }, [toggleVideoEnabled]);

  const classes = useStyles();

  let tooltipMessage = "";

  if (isVideoEnabled) {
    tooltipMessage = translate("disableCamera");
  }

  if (!isVideoEnabled) {
    tooltipMessage = translate("enableCamera");
  }

  const renderIcons = () => {
    if (isVideoEnabled)
      return (
        <FiVideo
          size={18}
          color="#475467"
          className={classes.iconPointer}
          data-cy="videoSurveyEnabledVideoIcon"
        />
      );

    return (
      <FiVideoOff
        size={18}
        color="#475467"
        className={classes.iconPointer}
        data-cy="videoSurveyDisabledVideoIcon"
      />
    );
  };

  return (
    <Tooltip title={tooltipMessage}>
      <span>
        <IconButton
          onClick={toggleVideo}
          className={classes.iconButton}
          disabled={!hasVideoInputDevices || props.disabled}
          data-cy="videoSurveyToggleVideoIcon"
          data-cy-video-toggle
        >
          {renderIcons()}
        </IconButton>
      </span>
    </Tooltip>
  );
}
